/**
 * Home (TMMAPP-0152 §A; prototype states 01–09, P:88–183) at `/me/home`, the landing page for
 * everyone. A calm daily start whose scope is one fixed rule (`homeScopeOf`, TMMAPP-0159 §C; Viewing
 * is removed): a subscriber's Home is their own money and every meeting they're in; a guest's is the
 * first virtual account shared with them, with its chip in the top bar. Every figure is served or
 * counted from served rows; what nothing serves says **Coming soon**.
 *
 * - **Layout:** two columns at `lg` — the main column (flex 560) and a 300 px side column — and one
 *   column below `lg`, with the side cards after the greeting.
 * - **The greeting** (`display`): "Good morning / afternoon / evening, {name}." by the hour in the
 *   person's `/me` zone; `{name}` is the whole `/me` display name, and on a guest's Home
 *   "{name} & {owner}". The lead is the person's own or the space's.
 * - **Mood and intent** (`HomeChoices`) stay in this visit's `sessionStorage`; nothing is sent.
 * - **Suggested for you** (`HomeSuggestions`): four tiles for the intent.
 * - **Shared with you** (`SharedSpaceCards`): a card per shared virtual account, hidden when none.
 * - **This week at a glance:** Bills coming up is a COUNT of outgoing planned occurrences in the
 *   next 7 days (never a money total, MM-D-53); Spending and Savings are Coming soon; Your focus is
 *   the focus goal (`homeFocus`). **Money Tools** opens `/me/tools`.
 * - **The side column** (`HomeSide`): Today's gentle nudge (a rule list) and Coming up.
 */
import { useState, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { Box, Button, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { V2Tile } from '@/components/V2Tile';
import { isOwnerAccount } from '@/features/accounts/accountAccess';
import { nextMeetingOf } from '@/features/meetings/nextMeeting';
import { formatList, formatNumber } from '@/i18n/format';
import { personName } from '@/i18n/personName';
import { useLocale } from '@/i18n/useLocale';
import { todayCivilInZone } from '@/i18n/zonedCivil';
import { useMe } from '@/session/meHooks';
import { useComingSoon } from '@/shell/useComingSoon';
import { usePageTitle } from '@/shell/usePageTitle';
import type { Mood } from '@/theme/categorical';
import { resolveTokens } from '@/theme/theme';
import { IntentCard, MoodCard } from './HomeChoices';
import { focusViewOf, type FocusView } from './homeFocus';
import {
  billsCountOfOccurrences,
  billsCountOfPlan,
  billsWindow,
  dayPartAt,
  defaultIntent,
  dismissNudge,
  nudgeDismissedToday,
  nudgeOf,
  readStoredIntent,
  readStoredMood,
  writeStoredIntent,
  writeStoredMood,
  type Intent,
  type Nudge,
} from './homeRules';
import { homeScopeOf, sharedSpaces, type HomeScope } from './homeScope';
import { SharedSpaceCards } from './HomeShared';
import { ComingUpCard, NudgeCard } from './HomeSide';
import { HomeSuggestions } from './HomeSuggestions';
import { dataOf, useHomeData, type HomeData } from './useHomeData';

/** Who the greeting names: the person, and on a guest's Home the space's people too. */
function greetingName(scope: HomeScope, name: string, t: TFunction, locale: string): string {
  if (scope.kind === 'me') return name;
  const others =
    scope.audience.kind === 'sharedWith'
      ? scope.audience.names
      : [personName(scope.audience.owner, t)];
  return others.length === 0
    ? name
    : t('home.together', { name, names: formatList(others, locale) });
}

function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
      <Typography variant="h3" component="h2" sx={{ flex: 1 }}>
        {children}
      </Typography>
      {action}
    </Box>
  );
}

/** A glance tile's figure: `stat`, in the prototype's `brand` text (`link`). */
function Figure({ children }: { children: ReactNode }) {
  const tokens = resolveTokens(useTheme());
  return (
    <Typography
      variant="stat"
      component="span"
      sx={{ display: 'block', color: tokens.link, fontVariantNumeric: 'tabular-nums' }}
    >
      {children}
    </Typography>
  );
}

function Glance({
  scope,
  data,
  owner,
  today,
  focus,
}: {
  scope: HomeScope;
  data: HomeData;
  owner: boolean;
  today: string;
  focus: FocusView | null | undefined;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const week = billsWindow(today);
  const loading = <Skeleton variant="text" width={60} />;

  // A count, never a sum: the space's plan, or the person's own planned occurrences.
  const billsRead = scope.kind === 'space' ? data.plan.read : owner ? data.planned.read : null;
  let bills: ReactNode = '—';
  let billsSub: string | undefined = t('home.glance.billsSub');
  if (billsRead?.status === 'loading') bills = loading;
  else if (billsRead?.status === 'error') billsSub = t('home.glance.unavailable');
  else if (scope.kind === 'space' && data.plan.read.status === 'ready') {
    bills = formatNumber(billsCountOfPlan(data.plan.read.data, week), locale);
  } else if (scope.kind === 'me' && data.planned.read.status === 'ready') {
    bills = formatNumber(billsCountOfOccurrences(data.planned.read.data, week), locale);
  } else billsSub = undefined;

  return (
    <Stack component="section" aria-labelledby="home-glance" sx={{ gap: 1.5 }}>
      <SectionTitle
        action={
          <Button
            component={RouterLink}
            to="/me/tools"
            size="small"
            sx={{ fontWeight: 600, fontSize: 13, color: tokens.link, textTransform: 'none' }}
          >
            {t('home.glance.tools')}
          </Button>
        }
      >
        <span id="home-glance">{t('home.glance.title')}</span>
      </SectionTitle>
      <Box
        data-glance=""
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: 'repeat(auto-fill, minmax(min(100%, 180px), 1fr))',
          },
          gap: 1.5,
          '& [data-tile]': { minWidth: 0 },
        }}
      >
        <V2Tile label={t('home.glance.bills')} value={<Figure>{bills}</Figure>} sub={billsSub} />
        <V2Tile
          label={t('home.glance.spending')}
          value={<Figure>{t('comingSoon.title')}</Figure>}
        />
        <V2Tile
          label={t(scope.kind === 'space' ? 'home.glance.sharedSavings' : 'home.glance.savings')}
          value={<Figure>{t('comingSoon.title')}</Figure>}
        />
        <V2Tile
          to={focus == null ? '/me/meetings' : focus.to}
          label={t('home.glance.focus')}
          value={
            <Figure>
              {focus === undefined
                ? loading
                : focus === null
                  ? t('home.glance.focusNone')
                  : (focus.pct ?? '—')}
            </Figure>
          }
          sub={focus == null ? undefined : focus.name}
        />
      </Box>
    </Stack>
  );
}

export function HomePage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const me = useMe();
  const zone = me.user.timezone ?? 'UTC';
  const today = todayCivilInZone(zone);
  const owner = me.tier === 'SUBSCRIBER';
  // One fixed rule (§C 9): a guest's first shared virtual account by `/me` order, else their own.
  const scope = homeScopeOf(me);
  const shared = sharedSpaces(me.virtualAccounts);
  usePageTitle({ title: t('nav.home') });
  const data = useHomeData({ scope, owner, today });
  const comingSoon = useComingSoon();

  const [mood, setMood] = useState<Mood | null>(readStoredMood);
  // Stored only once picked: until then the intent follows the scope's default.
  const [picked, setPicked] = useState<Intent | null>(readStoredIntent);
  const intent = picked ?? defaultIntent(scope);
  const [dismissedOn, setDismissedOn] = useState<string | null>(() =>
    nudgeDismissedToday(today) ? today : null,
  );

  const pickMood = (next: Mood) => {
    writeStoredMood(next);
    setMood(next);
  };
  const pickIntent = (next: Intent) => {
    writeStoredIntent(next);
    setPicked(next);
  };

  // The page's moment: read once when Home opens (the greeting, the nudge's 36 hours, what's past).
  const [now] = useState(() => Date.now());
  const meetings = data.meetings.read;
  const nextMeeting =
    meetings.status === 'ready'
      ? nextMeetingOf(meetings.data.items, now)
      : meetings.status === 'error'
        ? null
        : undefined;
  const ownAccounts =
    dataOf(data.accounts.read)?.filter(
      (account) => isOwnerAccount(account) && account.status !== 'CLOSED',
    ) ?? null;
  const focus = focusViewOf(data, me.virtualAccounts, t, locale);

  // The owner-side rules read the person's own books (MM-D-43): a subscriber's Home only.
  const ownBooks = owner;
  const waiting =
    nextMeeting === undefined ||
    (ownBooks && (data.review.read.status === 'loading' || data.summary.read.status === 'loading'));
  const nudge: Nudge | undefined =
    dismissedOn === today
      ? { kind: 'calm' }
      : waiting
        ? undefined
        : nudgeOf({
            meeting: nextMeeting,
            reviewTotal: ownBooks ? (dataOf(data.review.read)?.total ?? null) : null,
            unallocatedLines: ownBooks
              ? (dataOf(data.summary.read)?.unallocatedLineCount ?? null)
              : null,
            now,
          });

  const name = greetingName(scope, me.user.displayName, t, locale);

  return (
    <Box
      data-home={scope.kind}
      sx={{
        display: 'grid',
        gap: { xs: 3, lg: '26px 28px' },
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 300px' },
        gridTemplateAreas: { xs: '"greet" "side" "main"', lg: '"greet side" "main side"' },
        alignItems: 'start',
      }}
    >
      <Box sx={{ gridArea: 'greet' }}>
        <Typography
          id="page-title"
          tabIndex={-1}
          variant="display"
          component="h1"
          sx={{ outline: 'none' }}
        >
          {t(`home.greeting.${dayPartAt(new Date(now), zone)}`, { name })}
        </Typography>
        <Typography
          component="p"
          sx={{ m: 0, mt: 1, fontSize: 16, lineHeight: '23px', color: tokens.text2, maxWidth: 560 }}
        >
          {t(scope.kind === 'space' ? 'home.leadSpace' : 'home.leadMe')}
        </Typography>
      </Box>

      <Stack
        data-home-side=""
        sx={{ gridArea: 'side', gridRow: { lg: 'span 2' }, gap: 2, alignSelf: 'start' }}
      >
        <NudgeCard
          nudge={nudge}
          timeZone={zone}
          today={today}
          onDismiss={() => {
            dismissNudge(today);
            setDismissedOn(today);
          }}
        />
        <ComingUpCard
          meetings={meetings}
          next={nextMeeting ?? null}
          onRetry={data.meetings.retry}
          timeZone={zone}
        />
      </Stack>

      <Stack data-home-main="" sx={{ gridArea: 'main', gap: '26px', minWidth: 0 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
            gap: 2,
            alignItems: 'start',
          }}
        >
          <MoodCard mood={mood} intent={intent} onMood={pickMood} onIntent={pickIntent} />
          <IntentCard intent={intent} onIntent={pickIntent} />
        </Box>

        <Stack component="section" aria-labelledby="home-suggested" sx={{ gap: 1.5 }}>
          <SectionTitle>
            <span id="home-suggested">{t('home.suggest.title')}</span>
          </SectionTitle>
          <HomeSuggestions
            intent={intent}
            scope={scope}
            data={data}
            focus={focus}
            ownAccounts={ownAccounts}
            nextMeeting={nextMeeting}
            onComingSoon={comingSoon.open}
          />
        </Stack>

        {shared.length > 0 && (
          <Stack component="section" aria-labelledby="home-shared" sx={{ gap: 1.5 }}>
            <SectionTitle>
              <span id="home-shared">{t('home.shared.title')}</span>
            </SectionTitle>
            <SharedSpaceCards
              spaces={shared}
              meetings={data.myMeetings.read}
              now={now}
              timeZone={zone}
            />
          </Stack>
        )}

        <Glance scope={scope} data={data} owner={owner} today={today} focus={focus} />
      </Stack>
      {comingSoon.node}
    </Box>
  );
}
